/**
 * Telemetry SDK wiring — ACTIVE (Track C, Phase 5 go).
 *
 * Initialises the `@aplicy-com/hp-telemetry-web` SDK for the customer portal
 * and exposes guarded `track` / `pageView` / `pageLeft` / `writeConsent`
 * wrappers.
 *
 * Consent (ADR-4): analytics is ON by default (non-blocking, opt-out). The
 * banner (`src/components/CookieConsentBanner.tsx`) persists consent to the
 * `hp_consent` cookie — the exact cookie `src/consent.js` in the SDK reads —
 * so the SDK's consent gate sees the state before the first flush. A consent
 * VERSION string is persisted alongside and surfaced on every event as
 * `context.consent.version`.
 *
 * NOTE on `version` persistence: the installed SDK is 0.1.1, whose
 * `setConsent()` re-writes the cookie WITHOUT the version field (that was
 * added in SDK 0.1.2). The banner therefore writes the full cookie (including
 * `version`) itself and then dispatches the SDK's `hp:consent-changed` DOM
 * event, which triggers the consent-gate flush on both 0.1.1 and 0.1.2.
 */

import { init, track as sdkTrack } from '@aplicy-com/hp-telemetry-web';

/** Feature flag — Track C activation. */
export const TELEMETRY_ENABLED: boolean = true;

/** Originating app — maps to the schema-registry domain (`customer`). */
export const TELEMETRY_APP_ID = 'customer-portal';

/** Deployment environment: 'prod' | 'uat' | 'dev'. */
export const TELEMETRY_ENV: string = import.meta.env.VITE_NODE_ENV ?? 'dev';

/**
 * Collector endpoint (Phase 5 go). REQUIRED — set VITE_TELEMETRY_COLLECTOR_URL
 * in the Amplify console (see .env.example). There is deliberately NO working
 * fallback: the production build fails if this is missing, rather than
 * silently posting to the page origin (`/api/v1/collect` → 404).
 */
export const TELEMETRY_COLLECTOR_URL: string = import.meta.env
  .VITE_TELEMETRY_COLLECTOR_URL;

/**
 * ADR-4 consent version — bump whenever the banner text changes so returning
 * users are re-prompted (see CookieConsentBanner).
 */
export const TELEMETRY_CONSENT_VERSION = '1';

/** Consent cookie name — must match hp-telemetry-web/src/consent.js. */
export const CONSENT_COOKIE = 'hp_consent';

/** SDK consent-changed DOM event — hp-telemetry-web/src/consent.js. */
export const CONSENT_CHANGED_EVENT = 'hp:consent-changed';

let _initialised = false;

type ConsentRecord = {
  analytics: boolean;
  marketing: boolean;
  version: string | null;
};

function readConsentCookie(): ConsentRecord | null {
  try {
    const prefix = `${CONSENT_COOKIE}=`;
    const part = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(prefix));
    if (!part) return null;
    const raw = part.slice(prefix.length);
    const decoded = (() => {
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    })();
    const parsed = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object') {
      return {
        analytics: parsed.analytics === true,
        marketing: parsed.marketing === true,
        version: typeof parsed.version === 'string' ? parsed.version : null,
      };
    }
  } catch {
    /* ignore malformed cookie */
  }
  return null;
}

/**
 * Read the current consent. Defaults to the ADR-4 default-on regime
 * (analytics on, marketing on) when no cookie has been persisted yet.
 */
export function readConsent(): ConsentRecord {
  const existing = readConsentCookie();
  if (existing) return existing;
  return { analytics: true, marketing: true, version: null };
}

/**
 * Persist consent to the `hp_consent` cookie AND notify the SDK's consent
 * gate. Writes the cookie directly (rather than relying on the SDK 0.1.1
 * `setConsent()`, which drops the `version` field) then dispatches
 * `hp:consent-changed` so queued events flush on 0.1.1 and 0.1.2.
 */
export function writeConsent(consent: {
  analytics: boolean;
  marketing?: boolean;
  version?: string;
}): void {
  const normalized = {
    analytics: consent.analytics === true,
    marketing: consent.marketing === true,
    ...(typeof consent.version === 'string' && consent.version
      ? { version: consent.version }
      : {}),
  };
  try {
    const maxAge = 60 * 60 * 24 * 395; // ~13 months, mirrors SDK consent.js
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(
      JSON.stringify(normalized)
    )}; max-age=${maxAge}; path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
  try {
    document.dispatchEvent(
      new CustomEvent(CONSENT_CHANGED_EVENT, {
        detail: {
          analytics: normalized.analytics,
          marketing: normalized.marketing,
        },
      })
    );
  } catch {
    /* ignore */
  }
}

/**
 * Remove the `hp_consent` cookie. Used when a persisted consent predates the
 * current consent VERSION (ADR-4: re-prompt on version change) — clearing it
 * makes the SDK treat consent as undecided (queue, don't drop) and lets the
 * banner re-prompt, rather than silently carrying a stale opt-out forward.
 */
function clearConsentCookie(): void {
  try {
    document.cookie = `${CONSENT_COOKIE}=; max-age=0; path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

/**
 * ADR-4 default-on: persist consent BEFORE init() so the SDK reads it for the
 * very first event (no queueing).
 *
 * A stale consent whose VERSION no longer matches the current banner is
 * cleared (not re-seeded) — a previous opt-out must not be silently flipped
 * back on. The SDK then treats consent as undecided and queues events until
 * the banner re-prompts and the user re-confirms.
 */
function ensureDefaultConsent(): void {
  const existing = readConsentCookie();
  if (!existing) {
    writeConsent({
      analytics: true,
      marketing: true,
      version: TELEMETRY_CONSENT_VERSION,
    });
    return;
  }
  if (existing.version !== TELEMETRY_CONSENT_VERSION) {
    clearConsentCookie();
  }
}

/**
 * Initialise telemetry. Call once at app start (before React mounts) so the
 * SDK reads the persisted consent cookie before the first event flush.
 */
export function initTelemetry(): void {
  if (!TELEMETRY_ENABLED || _initialised) {
    return;
  }
  _initialised = true;

  ensureDefaultConsent();

  init({
    app: TELEMETRY_APP_ID,
    env: TELEMETRY_ENV,
    collectorUrl: TELEMETRY_COLLECTOR_URL,
    consentVersion: TELEMETRY_CONSENT_VERSION,
    // debug: false,
  });

  // Acquisition attribution — first event of the session (session.started).
  sdkTrack('session.started', {
    entryPath: window.location.pathname,
    referrerHost: (() => {
      try {
        return document.referrer
          ? new URL(document.referrer).hostname
          : 'direct';
      } catch {
        return 'direct';
      }
    })(),
    campaign: (() => {
      try {
        const p = new URLSearchParams(window.location.search);
        const utm: Record<string, string> = {};
        let has = false;
        for (const k of ['source', 'medium', 'campaign', 'content', 'term']) {
          const v = p.get(`utm_${k}`);
          if (v) {
            utm[k] = v;
            has = true;
          }
        }
        return has ? utm : undefined;
      } catch {
        return undefined;
      }
    })(),
  });
}

/**
 * Guarded track wrapper. No-op unless telemetry is enabled AND initialised.
 */
export function track(
  eventName: string,
  attributes?: Record<string, unknown>
): void {
  if (!TELEMETRY_ENABLED || !_initialised) {
    return;
  }
  sdkTrack(eventName, attributes || {});
}

/** Convenience wrapper for `page.viewed`. */
export function pageView(
  template: string,
  path: string,
  loadMs?: number
): void {
  track('page.viewed', {
    template,
    path,
    ...(typeof loadMs === 'number' ? { loadMs } : {}),
  });
}

/** Convenience wrapper for `page.left`. */
export function pageLeft(attributes: Record<string, unknown>): void {
  track('page.left', attributes);
}
